import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import PlanInfoCard from '../../../components/organisms/cards/PlanInfoCard';
import Paragraph from '../../atoms/paragraphs/Paragraph';
import moment from 'moment';
import 'moment/locale/pt';
import 'moment/locale/es';
import { Tooltip } from 'react-tooltip';
import Icon from '../../atoms/icons/Icon';
import { ReactComponent as IxLoading } from '../../../assets/imgs/ix-loading.svg';

/**
 * PlanSelector component for selecting subscription plans
 * @param {object} props - React props object
 * @returns {React.ReactElement} PaymentMethodSelector component
 */
const CheckoutSummary = ({ intl, ...props }) => {
  const {
    new_plan,
    old_plan,
    cycle,
    old_cycle,
    upgrade,
    summary,
    className = '',
    isLoading = true,
    preconditionError,
  } = props;
  const plan_align = upgrade
    ? 'justify-content-between'
    : 'justify-content-center';

  const new_plan_title = upgrade
    ? intl.messages['checkoutSummaryNewPlanTitle']
    : intl.messages['checkoutSummaryCurrentPlanTitle'];

  const [warningMessage, setWarningMessage] = useState(preconditionError);

  useEffect(() => {
    setWarningMessage(preconditionError);
  }, [preconditionError]);

  return (
    <div className={`d-flex flex-row checkout-summary-container ${className}`}>
      {(isLoading && (
        <div className='checkout-summary-loader-wrapper d-flex flex-grow-1 w-100 justify-content-center align-items-center'>
          <div className='checkout-summary-loader-container'>
            <IxLoading />
          </div>
        </div>
      )) ||
        (warningMessage && (
          <div className={`d-flex flex-column checkout-summary-wrapper w-100`}>
            <div className={`d-flex flex-row w-100 ${plan_align}`}>
              {(upgrade && (
                <>
                  <div className='d-flex flex-column checkout-summary-content'>
                    <Paragraph className='text-header h5 mb-2'>
                      {intl.messages['currentPlanFlag'] + ':'}
                    </Paragraph>
                    <PlanInfoCard
                      plan={old_plan}
                      cycle={old_cycle}
                      className='p-2 w-auto checkout-summary-plan-info-card'
                      inSummary={true}
                      onClick={() => {}}
                    />
                  </div>

                  <div className='sep-light vertical'></div>
                </>
              )) || <></>}
              <div className='d-flex flex-column checkout-summary-content'>
                <Paragraph className='text-header plan-selected h5 mb-2'>
                  {new_plan_title + ':'}
                </Paragraph>
                <PlanInfoCard
                  plan={new_plan}
                  selected_plan={true}
                  cycle={cycle}
                  className='p-2 w-auto checkout-summary-plan-info-card'
                  inSummary={true}
                  onClick={() => {}}
                />
              </div>
            </div>

            <div className='checkout-summary-totals mt-4 w-100'>
              <div className='d-flex flex-row justify-content-between'>
                <Paragraph className='text-header mb-3 h4'>
                  {intl.messages['checkoutSummaryNewSubscriptionTitle']}
                </Paragraph>
              </div>

              <div className='d-flex flex-column summary-total'>
                <div className='d-flex flex-row justify-content-between'>
                  <Paragraph className='text-paragraph'>
                    {intl.messages['checkoutSummarySubscriptionStart']}
                  </Paragraph>
                  <Paragraph className='text-paragraph'>
                    {moment(summary?.start_date)
                      .locale(intl.locale)
                      .format('DD MMM YYYY')}
                  </Paragraph>
                </div>
              </div>

              <div className='d-flex flex-column summary-total'>
                <div className='d-flex flex-row justify-content-between'>
                  <Paragraph className='text-paragraph'>
                    {intl.messages['checkoutSummarySubscriptionChange']}
                  </Paragraph>
                  <Paragraph className='text-paragraph'>
                    {moment().locale(intl.locale).format('DD MMM YYYY')}
                  </Paragraph>
                </div>
              </div>

              <div className='d-flex flex-column'>
                <div className='d-flex flex-row justify-content-between'>
                  <Paragraph className='text-paragraph'>
                    {intl.messages['checkoutSummaryNextRenew']}
                  </Paragraph>
                  <Paragraph className='text-paragraph'>
                    {moment(summary?.end_date)
                      .locale(intl.locale)
                      .format('DD MMM YYYY')}
                  </Paragraph>
                </div>
              </div>
              <div className='upgrade-message-wrapper d-flex flex-grow-1 px-3 py-2 mt-3'>
                <Icon className='upgrade-message-icon icon fas fa-2x fa-exclamation-triangle my-auto' />
                <Paragraph className='text-paragraph upgrade-message text-center ps-3 m-auto'>
                  {intl.messages['preconditionError']}
                </Paragraph>
              </div>
            </div>
          </div>
        )) || (
          <div className={`d-flex flex-column checkout-summary-wrapper w-100`}>
            <div className={`d-flex flex-row w-100 ${plan_align}`}>
              {(upgrade && (
                <>
                  <div className='d-flex flex-column checkout-summary-content'>
                    <Paragraph className='text-header h5 mb-2'>
                      {intl.messages['currentPlanFlag'] + ':'}
                    </Paragraph>
                    <PlanInfoCard
                      plan={old_plan}
                      cycle={old_cycle}
                      className='p-2 w-auto checkout-summary-plan-info-card'
                      inSummary={true}
                      onClick={() => {}}
                    />
                  </div>

                  <div className='sep-light vertical'></div>
                </>
              )) || <></>}
              <div className='d-flex flex-column checkout-summary-content'>
                <Paragraph className='text-header plan-selected h5 mb-2'>
                  {new_plan_title + ':'}
                </Paragraph>
                <PlanInfoCard
                  plan={new_plan}
                  selected_plan={true}
                  cycle={cycle}
                  className='p-2 w-auto checkout-summary-plan-info-card'
                  inSummary={true}
                  onClick={() => {}}
                />
              </div>
            </div>

            <div className='checkout-summary-totals mt-4 w-100'>
              {(upgrade && (
                <div className='upgrade-message-wrapper d-flex flex-grow-1 px-3 py-2 mb-3'>
                  <Icon className='upgrade-message-icon icon fas fa-2x fa-exclamation-triangle my-auto' />
                  <Paragraph className='text-paragraph upgrade-message text-center ps-2 my-0'>
                    {cycle === 'month' ? (
                      intl.messages['checkoutSummaryUpgradeMonthlyMessage']
                    ) : (
                      <FormattedMessage
                        id='checkoutSummaryUpgradeMessage'
                        values={{
                          months: summary?.new_subscription_remaining_months,
                        }}
                      />
                    )}
                  </Paragraph>
                </div>
              )) || <></>}

              <div className='d-flex flex-row justify-content-between'>
                <Paragraph className='text-header mb-3 h4'>
                  {intl.messages['checkoutSummaryNewSubscriptionTitle']}
                </Paragraph>
              </div>

              <div className='d-flex flex-column summary-total'>
                <div className='d-flex flex-row justify-content-between'>
                  <Paragraph className='text-paragraph'>
                    {intl.messages['checkoutSummarySubscriptionStart']}
                  </Paragraph>
                  <Paragraph className='text-paragraph'>
                    {moment(summary?.start_date)
                      .locale(intl.locale)
                      .format('DD MMM YYYY')}
                  </Paragraph>
                </div>
              </div>

              <div className='d-flex flex-column summary-total'>
                <div className='d-flex flex-row justify-content-between'>
                  <Paragraph className='text-paragraph'>
                    {intl.messages['checkoutSummarySubscriptionChange']}
                  </Paragraph>
                  <Paragraph className='text-paragraph'>
                    {moment().locale(intl.locale).format('DD MMM YYYY')}
                  </Paragraph>
                </div>
              </div>

              <div className='d-flex flex-column'>
                <div className='d-flex flex-row justify-content-between'>
                  <Paragraph className='text-paragraph'>
                    {intl.messages['checkoutSummaryNextRenew']}
                  </Paragraph>
                  <Paragraph className='text-paragraph'>
                    {moment(summary?.end_date)
                      .locale(intl.locale)
                      .format('DD MMM YYYY')}
                  </Paragraph>
                </div>
              </div>

              <div className='sep-light'></div>

              <div className='mt-3'>
                <div className='d-flex flex-row justify-content-between'>
                  <Paragraph className='text-header mb-3 h4'>
                    {intl.messages['checkoutSummaryTotal']}
                  </Paragraph>
                </div>

                <div className='d-flex flex-column summary-total'>
                  <div className='d-flex flex-row justify-content-between'>
                    <Paragraph className='text-paragraph'>
                      {intl.messages['checkoutSummaryPlanCost']}
                    </Paragraph>
                    <Paragraph
                      className='text-paragraph'
                      data-tooltip-id='price_tooltip'
                      data-tooltip-delay-hide={250}
                    >
                      {summary?.plan_value}€
                    </Paragraph>

                    <Tooltip
                      id='price_tooltip'
                      className='tooltip'
                      content={
                        summary?.new_subscription_remaining_months +
                        ' x ' +
                        summary?.price +
                        ' = ' +
                        summary?.plan_value +
                        '€'
                      }
                      place='bottom'
                    />
                  </div>
                </div>

                {summary?.discount_coupon !== undefined &&
                  summary?.discount_coupon > 0 && (
                    <div className='d-flex flex-column'>
                      <div className='d-flex flex-row justify-content-between'>
                        <Paragraph className='text-paragraph'>
                          {intl.messages['checkoutSummaryDiscountCoupon']}
                        </Paragraph>
                        <Paragraph
                          className='text-paragraph'
                          data-tooltip-id='coupon_tooltip'
                          data-tooltip-delay-hide={250}
                        >
                          -{summary?.discount_coupon}€
                        </Paragraph>

                        <Tooltip
                          id='coupon_tooltip'
                          className='tooltip'
                          content={
                            summary?.plan_value +
                            ' x ' +
                            summary?.discount_percentage / 100 +
                            ' = ' +
                            summary?.discount_coupon +
                            '€'
                          }
                          place='bottom'
                        />
                      </div>
                    </div>
                  )}

                {(summary?.credit !== undefined && summary?.credit !== 0 && (
                  <div className='d-flex flex-column'>
                    <div className='d-flex flex-row justify-content-between'>
                      <Paragraph className='text-paragraph'>
                        {intl.messages['checkoutSummaryPlanCredit']}
                      </Paragraph>
                      <Paragraph
                        className='text-paragraph'
                        data-tooltip-id='credit_tooltip'
                        data-tooltip-delay-hide={250}
                      >
                        -{summary?.credit}€
                      </Paragraph>

                      <Tooltip
                        id='credit_tooltip'
                        className='tooltip'
                        content={
                          summary?.current_subscription_remaining_months +
                          ' x ' +
                          summary?.previous_price +
                          ' = ' +
                          summary?.credit +
                          '€'
                        }
                        place='bottom'
                      />
                    </div>
                  </div>
                )) || <></>}
              </div>

              <div className='sep-light'></div>

              <div className='mt-3'>
                {(summary?.plan_value !== summary?.amount_without_vat && (
                  <div className='d-flex flex-column'>
                    <div className='d-flex flex-row justify-content-between'>
                      <Paragraph className='text-paragraph'>
                        {intl.messages['checkoutSummaryTotalNoIva']}
                      </Paragraph>
                      <Paragraph className='text-paragraph'>
                        {summary?.amount_without_vat}€
                      </Paragraph>
                    </div>
                  </div>
                )) || <></>}

                <div className='d-flex flex-row justify-content-between'>
                  <Paragraph className='text-header h4'>
                    {intl.messages['checkoutSummaryTotalIva']}
                  </Paragraph>
                  <Paragraph className='text-header h4'>
                    {summary?.amount_with_vat}€
                  </Paragraph>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default injectIntl(CheckoutSummary);
